import {
  Duration,
  addMinutes,
  formatRelative as dateFnsFormatRelative,
  differenceInMinutes,
  format,
  formatDistanceToNow,
  parse,
  parseISO,
  set,
  sub,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import { computed } from 'vue';
import tzLookup from '@photostructure/tz-lookup';
import config from '@/config';
import { localeFormats } from '@/utils/locale';
import { t } from '@/utils/i18n';

// ─── Locale ───────────────────────────────────────────────────────────────────

// date-fns asks the locale for a format pattern per relative-time token. The
// patterns carry literal words ("Today at"), so they are translated rather
// than hardcoded, otherwise a non-English locale would render English
// scaffolding around localised day names.
const formatRelativeToken = (token: string): string =>
  t(`base.dateRelative.${token}`);

const timeFormat = (includeSeconds: boolean): string => {
  if (localeFormats.value.use24Hour) {
    return includeSeconds ? 'HH:mm:ss.SSS' : 'HH:mm';
  }
  return includeSeconds ? 'h:mm:ss.SSS a' : 'h:mm a';
};

// Each date-fns locale bakes in its own time convention (enUS's 'p' token is
// 12-hour, enGB's is 24-hour) - override formatLong.time so 'p' follows the
// active locale's use24Hour, the same as formatTime/formatDateTime, whichever
// locale supplies the day and month names.
const dateFnsLocale = computed(() => {
  const { dateFns } = localeFormats.value;

  return {
    ...dateFns,
    formatRelative: formatRelativeToken,
    formatLong: {
      ...dateFns.formatLong,
      time: () => timeFormat(false),
    },
  };
});

// PrimeVue's DatePicker takes jQuery-UI-style tokens rather than date-fns
// ones, so a date-fns pattern has to be rewritten before it can be handed
// over. Note 'yy' there means a four-digit year, not a two-digit one.
const primeVueTokens: Record<string, string> = {
  yyyy: 'yy',
  MM: 'mm',
  dd: 'dd',
};

// Unknown tokens throw rather than passing through: PrimeVue renders a token
// it does not recognise as a literal, so a silent pass-through would put stray
// letters in the date field instead of failing.
export const toPrimeVueDateFormat = (pattern: string): string =>
  pattern.replace(/[A-Za-z]+/g, (token) => {
    const converted = primeVueTokens[token];

    if (!converted) {
      throw new Error(`No PrimeVue equivalent for date token '${token}'`);
    }

    return converted;
  });

export const primeVueDateFormat = computed(() =>
  toPrimeVueDateFormat(localeFormats.value.dateShort),
);

// ─── Timestamps ───────────────────────────────────────────────────────────────

export const nowUnixTimestamp = (): number =>
  Math.round(new Date().getTime() / 1000);

export const nowSubtract = (duration: Duration): Date =>
  sub(new Date(), duration);

// ─── Parsing ──────────────────────────────────────────────────────────────────

export const dateParse = (
  value: string,
  dateFormat = localeFormats.value.dateShort,
): Date => parse(value, dateFormat, new Date());

export const dateParseISO = (value: string): Date => parseISO(value);

// ─── Formatting ───────────────────────────────────────────────────────────────

export const formatTime = (value: Date, includeSeconds = false): string =>
  format(value, timeFormat(includeSeconds));

export const formatDateTime = (value: Date, includeSeconds = false): string =>
  format(
    value,
    `${localeFormats.value.dateShort} ${timeFormat(includeSeconds)}`,
  );

export const formatDateTimeToIso = (value: Date, includeTime = true): string =>
  format(value, includeTime ? "yyyy-MM-dd'T'HH:mm:ss.SSS" : 'yyyy-MM-dd');

// The backend always expects dd/MM/yyyy HH:mm regardless of the active
// brand/locale - this is a fixed wire format, not a display format, so it
// must not follow the active locale's display format.
export const formatBackendDateTime = (
  value: Date,
  includeSeconds = false,
): string =>
  format(
    value,
    includeSeconds ? 'dd/MM/yyyy HH:mm:ss.SSS' : 'dd/MM/yyyy HH:mm',
  );

export const formatLogDate = (value: Date): string => format(value, 'yyyyMMdd');

export const formatRelative = (value: Date): string =>
  dateFnsFormatRelative(value, new Date(), { locale: dateFnsLocale.value });

export const formatRelativeFromIso = (isoDateString: string): string =>
  formatRelative(dateParseISO(isoDateString));

export const formatIsoString = (iso: string): string =>
  formatDateTime(dateParseISO(iso));

export const formatJourneyDuration = (mins: number | undefined): string => {
  if (!mins) {
    return '';
  }

  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  const hour = t('base.hour');
  const minute = t('base.minute');
  let timeText = '';

  if (hours > 0 && minutes > 0) {
    timeText = hours + ' ' + hour + ' ' + minutes.toFixed(0) + ' ' + minute;
  } else if (hours > 0 && minutes === 0) {
    timeText = hours + ' ' + hour;
  } else {
    timeText = minutes.toFixed(0) + ' ' + minute;
  }

  return timeText;
};

// ─── Calculations ─────────────────────────────────────────────────────────────

export const toMinute = (value: Date): Date =>
  set(value, { seconds: 0, milliseconds: 0 });

export const getDateParts = (value: Date) => {
  const dateString = format(value, 'dd,MM,yy,HH,mm,ss');
  const [day, month, year, hour, minute, second] = dateString.split(',');
  return { day, month, year, hour, minute, second };
};

// ─── Timezone ─────────────────────────────────────────────────────────────────

export const getIsoOffsetfromCoordinates = (
  dateStr: string,
  lat?: number | null,
  long?: number | null,
) => {
  // Get timezone from coordinates
  const tz =
    lat != null && long != null
      ? tzLookup(lat, long)
      : config.app.fallbackTimezone;

  const utcDate = fromZonedTime(dateStr, tz);

  return formatInTimeZone(utcDate, tz, "yyyy-MM-dd'T'HH:mm:ssXXX");
};

// ─── Re-exports ───────────────────────────────────────────────────────────────

export { addMinutes, differenceInMinutes, formatDistanceToNow };
